// #region Libraries
import {
  Fragment,
  useContext,
  useEffect,
  useState,
} from "react";
import { Card, CardBody } from "@/components/bootstrap/TemporaryReactstrapCompatibilityLayer";
// #endregion

// #region Components
import Button from "../layout/Button";
import Loading from "../ui-elements/Loading";
import LabeledInputField from "../layout/LabeledInputField";
import ConfirmDeleteModal from "../management/seedTypes/ConfirmDeleteModal";
// #endregion

// #region Contexts
import SeedTypeManagementContext from "../../contexts/seed-type-management/SeedTypeManagementContext";
import AuthContext from "../../contexts/auth/AuthContext";
import ProcessContext from "../../contexts/process/ProcessContext";
import {
  applyMeasurementChange,
  areMeasurementsValid,
  commitMeasurementState,
  createMeasurementState,
  toMeasurementRequest,
} from "../../utility/seedVariantMeasurements";
import { buildSeedVariantTypeResetState } from "../../utility/seedVariantTypeEditorState";
// #endregion

/** * The SeedVariantTypeEditor component allows users to edit or create new seed variant types. *
 *  It integrates with the SeedTypeManagementContext for seed type operations and AuthContext for authentication-related data. *
 *  The component enables users to specify details for a seed variant type, including mandatory fields like LOT and Manufacturer, *
 *  and optional fields with logic to calculate missing values based on entered data. *
 *  @component
 *  @example * return <SeedVariantTypeEditor seedVariantType={seedVariantType} seedTypeId={seedTypeId} outerReset={outerReset} defaultEditOn={false} /> */
const SeedVariantTypeEditor = ({
  seedVariantType,
  seedTypeId,
  outerReset,
  defaultEditOn = false,
}) => {
  const {
    deleteSeedVariantType,
    updateSeedVariantType,
    saveNewSeedVariantType,
  } = useContext(SeedTypeManagementContext);
  const {
    loadAndSetAccessibleSeedTypesAsync,
    currentTenant,
    currentContainer,
  } = useContext(AuthContext);
  const { notifyError } = useContext(ProcessContext);

  const [loading, setLoading] = useState(false);
  const initialState = {
    lot: {
      value: seedVariantType?.lot ?? "",
      touched: false,
      validate: (text) => text?.length >= 3,
      isValid: !!seedVariantType?.lot,
    },
    manufacturer: {
      value: seedVariantType?.manufacturer ?? "",
      touched: false,
      validate: (text) => text?.length >= 3,
      isValid: !!seedVariantType?.manufacturer,
    },
    containerId: {
      value: seedVariantType?.containerId ?? currentContainer?.id,
      touched: false,
      validate: () => true,
      isValid: true,
    },
    seedTypeId: {
      value:
        seedTypeId ??
        (seedVariantType?.seedTypeId ? seedVariantType.seedTypeId : ""),
      touched: false,
      validate: () => true,
      isValid: true,
    },
    seedVariantTypeId: {
      value: seedVariantType?.id ?? "",
      touched: false,
      validate: () => true,
      isValid: true,
    },
  };

  const [edit, setEdit] = useState(defaultEditOn == true);
  const [seedVariantTypeState, setSeedVariantTypeState] =
    useState(initialState);
  const [tgw, setTgw] = useState(null);
  const [seedCount, setSeedCount] = useState(null);
  const [packageWeight, setPackageWeight] = useState(null);
  const [, setSeedConsumptionSoFar] = useState(undefined);
  const [isStateValid, setIsStateValid] = useState(false);

  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [showConfirmUpdate, setShowConfirmUpdate] = useState(false);


  useEffect(() => {
    if (!tgw || !packageWeight || !seedCount) {
      setIsStateValid(false);
      return;
    }

    setIsStateValid(
      Object.values(seedVariantTypeState).every((field) => field.isValid) &&
        areMeasurementsValid({ count: seedCount, tgw, packageWeight })
    );
  }, [tgw, packageWeight, seedCount, seedVariantTypeState]);


  // Initializes the component state based on the provided `seedVariantType` prop.
  // This includes setting up the initial values for the LOT, Manufacturer, TGW, Seed Count, and Package Weight,
  // and ensuring that the component state reflects the current seed variant type's properties.
  useEffect(() => {
    if (seedVariantType) {
      let calculatedSeedCount = 0;
      if (seedVariantType?.packageWeight && seedVariantType?.tgw) {
        calculatedSeedCount = Math.round(seedVariantType.packageWeight / seedVariantType.tgw * 1000);
        calculatedSeedCount -= seedVariantType.count;
      }

      setSeedConsumptionSoFar(calculatedSeedCount);
      const seedVariantTypeStateInit = Object.entries(seedVariantType).reduce(
        (acc, [key, value]) => {
          if (!(key in seedVariantTypeState)) return acc;
          return {
            ...acc,
            [key]: { ...acc[key], value, isValid: acc[key]?.validate(value) },
          };
        },
        initialState
      );

      setSeedVariantTypeState(seedVariantTypeStateInit);
      const measurements = createMeasurementState({
        count: seedVariantType?.count ?? "",
        tgw: seedVariantType?.tgw == null ? "" : seedVariantType.tgw * 1000,
        packageWeight: seedVariantType?.packageWeight == null ? "" : seedVariantType.packageWeight * 1000,
      });
      setTgw(measurements.tgw);
      setPackageWeight(measurements.packageWeight);
      setSeedCount(measurements.count);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seedVariantType]);

  // Enables the edit state, allowing the user to modify the current seed variant type's details.
  // This function is typically triggered by an "Edit" button or similar user action.
  const activateEditState = () => setEdit(true);

  // Constructs the request body for updating or creating a seed variant type.
  // This includes compiling the form data into the expected format for the backend service.
  const createRequestBody = () => {
    const updatedSeedVariantTypeProperties = Object.entries(
      seedVariantTypeState
    ).reduce((acc, [key, valueObject]) => {
      return { ...acc, [key]: valueObject.value };
    }, {});
    const updatedSeedVariantType = { ...updatedSeedVariantTypeProperties };
    return {
      ...updatedSeedVariantType,
      ...toMeasurementRequest({ count: seedCount, tgw, packageWeight }),
    };
  };

  // Handles the deletion of the current seed variant type.
  // It confirms the user's intent to delete and proceeds with the deletion if confirmed.
  const handleDelete = () => setShowConfirmDelete(true);
    const confirmDelete = async () => {
    setLoading(true);
    try {
      await deleteSeedVariantType(seedVariantType);
      await loadAndSetAccessibleSeedTypesAsync(
        currentTenant,
        currentContainer,
        true,
        "seedVariantTypes"
      );
    } catch (err) {
      console.error("Error: ", err);
      notifyError(
        "We have encountered an issue while deleting the seed variant type. Could you please logout, sign back in, and try again? If the error still exists, please contact the support team farmer@support.diecityfarm.de."
      );
    } finally {
      setLoading(false);
      setShowConfirmDelete(false);
    }
  };

  // Handles the update process for the seed variant type.
  // It confirms the user's intent to alter the seed type properties and performs the update operation if confirmed.
  const handleUpdate = async () => {
    if (!isStateValid) return;
    setShowConfirmUpdate(true);
  };
  const cancelUpdate = () => setShowConfirmUpdate(false);
  const confirmUpdate = async () => {
    setLoading(true);
    try {
      const updatedSeedVariantType = createRequestBody();
      await updateSeedVariantType(updatedSeedVariantType);
      await loadAndSetAccessibleSeedTypesAsync(
        currentTenant,
        currentContainer,
        true,
        "seedVariantTypes"
      );

      const initialFullSeedCount = updatedSeedVariantType.packageWeight && updatedSeedVariantType.tgw
        ? Math.round(updatedSeedVariantType.packageWeight / updatedSeedVariantType.tgw * 1000)
        : 0;
      setSeedConsumptionSoFar(initialFullSeedCount > 0 ? initialFullSeedCount - updatedSeedVariantType.count : 0);
      resetFromSource(updatedSeedVariantType);
    } catch (err) {
      notifyError(
        "We have encountered an issue while updating the seed variant type. Could you please logout, sign back in, and try again? If the error still exists, please contact the support team farmer@support.diecityfarm.de."
      );
    } finally {
      setLoading(false);
      setShowConfirmUpdate(false);
    }
  };

  // Creates a brand-new Seed Variant Type from the current form values.
  // Builds the request payload via createRequestBody()
  // Saves it via context action
  // Refreshes the accessible seed types list
  // Resets the form on success
  const handleSaveNew = async () => {
    setLoading(true);

    try {
      const newSeedVariantType = createRequestBody();
      await saveNewSeedVariantType(newSeedVariantType);
      await loadAndSetAccessibleSeedTypesAsync(
        currentTenant,
        currentContainer,
        true,
        "seedVariantTypes"
      );
      resetFromSource(); // clear form and exit edit mode
    } catch (err) {
      // Show a generic error toast/message on failure
      notifyError(
        "We have encountered an issue while saving the new seed variant type. Could you please logout, sign back in, and try again? If the error still exists, please contact the support team farmer@support.diecityfarm.de."
      );
    } finally {
      setLoading(false);
    }
  };

  // Handles generic changes to input fields by updating the corresponding state in the component.
  // This ensures any modifications to the form inputs are reflected in the component state, allowing for dynamic updates and validation.
  const onChange = (event) => {
    event.persist();
    const key = event.currentTarget.name;
    const value = event.currentTarget.value;
    setSeedVariantTypeState((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        value,
        isValid: prev[key].validate(value),
        touched: true,
      },
    }));
  };

  // Handles changes to the Package Weight input field, recalculating and updating the Seed Count
  // based on the new Package Weight and the existing TGW value. This ensures the Seed Count remains accurate and up-to-date.
  const updateMeasurements = (changedField, rawValue) => {
    const measurements = applyMeasurementChange(
      { count: seedCount, tgw, packageWeight },
      changedField,
      rawValue,
    );
    setSeedCount(measurements.count);
    setTgw(measurements.tgw);
    setPackageWeight(measurements.packageWeight);
  };

  const onPackageWeightChange = (event) => {
    updateMeasurements("packageWeight", event.currentTarget.value);
  };

  const onSeedCountChange = (event) => {
    updateMeasurements("count", event.currentTarget.value);
  };

  const onTgwChange = (event) => {
    updateMeasurements("tgw", event.currentTarget.value);
  };

  const onTgwBlur = () => {
    const measurements = commitMeasurementState({ count: seedCount, tgw, packageWeight });
    setSeedCount(measurements.count);
    setTgw(measurements.tgw);
    setPackageWeight(measurements.packageWeight);
  };

  // Resets the form to its initial state and exits the edit mode.
  // This can be used to cancel the editing process, reverting any changes made by the user.
  const resetFromSource = (source) => {
    const resetState = buildSeedVariantTypeResetState(source, initialState);
    setSeedVariantTypeState(resetState.fields);
    const measurements = resetState.measurements;
    setTgw(measurements.tgw);
    setPackageWeight(measurements.packageWeight);
    setSeedCount(measurements.count);
    setEdit(false);
    if (!!outerReset) outerReset();
  };

  // Button passes its click event to handlers. Cancel must never interpret
  // that event as a seed variant source.
  const handleCancel = () => resetFromSource(seedVariantType);

  return loading || !seedVariantType || !tgw || !seedCount || !packageWeight ? (
    <Loading fullScreen={true} displayText={""} />
  ) : (
    <>
      <ConfirmDeleteModal
        show={showConfirmDelete}
        onCancel={() => setShowConfirmDelete(false)}
        onConfirm={confirmDelete}
        header="DELETE SEED VARIANT TYPE"
        message={
          <>
            This will permanently remove this seed variant type.
            <br />
            This action cannot be undone.
          </>
        }
      />

      <ConfirmDeleteModal
        show={showConfirmUpdate}
        onCancel={cancelUpdate}
        onConfirm={confirmUpdate}
        header="ALTER SEED VARIANT TYPE"
        message={
          "Changing seed type properties will alter the data of every plant ever created with this seed type. Are you sure you want to continue with your changes?"
        }
        confirmLabel="Save"
        cancelLabel="Cancel"
      />

      <Card className="seed-variant-type-card">
        <CardBody className="seed-variant-type-card-body">
          <div className="lot-pos">
            <LabeledInputField
              labelText={"LOT"}
              inputName={"lot"}
              value={seedVariantTypeState.lot.value}
              isValid={seedVariantTypeState.lot.isValid}
              touched={seedVariantTypeState.lot.touched}
              inputType={"text"}
              onChange={onChange}
              placeholder={"LOT"}
              disabled={edit === false}
              errorMessage={"Minimum 3 characters."}
            />
          </div>
          <div className="manufacturer-pos">
            <LabeledInputField
              labelText={"Manufacturer"}
              inputName={"manufacturer"}
              value={seedVariantTypeState.manufacturer.value}
              isValid={seedVariantTypeState.manufacturer.isValid}
              touched={seedVariantTypeState.manufacturer.touched}
              inputType={"text"}
              onChange={onChange}
              placeholder={"Manufacturer"}
              disabled={edit === false}
              errorMessage={"Minimum 3 characters."}
            />
          </div>
          <div className="seed-count-pos">
            <LabeledInputField
              labelText={"Seed Count"}
              inputName={"count"}
              value={seedCount.value}
              isValid={seedCount.isValid}
              touched={seedCount.touched}
              inputType={"number"}
              onChange={onSeedCountChange}
              disabled={edit === false}
              errorMessage={"Must be greater then 0"}
              step={1}
            />
          </div>
          <div className="package-weight-pos">
            <LabeledInputField
              labelText={"Package Weight [g]"}
              inputName={"packageWeight"}
              value={packageWeight.value}
              isValid={packageWeight.isValid}
              touched={packageWeight.touched}
              inputType={"number"}
              onChange={onPackageWeightChange}
              disabled={edit === false}
              errorMessage={"Must be greater then 0"}
              step={0.1}
            />
          </div>
          <div className="tgw-pos">
            <LabeledInputField
              labelText={"TGW [g]"}
              inputName={"tgw"}
              value={tgw.value}
              isValid={tgw.isValid}
              touched={tgw.touched}
              inputType={"number"}
              onChange={onTgwChange}
              disabled={edit === false}
              errorMessage={"example: '1' or '1.0'"}
              step={0.1}
              onBlur={onTgwBlur}
            />
          </div>
        </CardBody>

        <div className="card-button seed-card-footer">
          {edit ? (
            <Fragment>
              <Button
                className="seed-card-btn"
                label="Cancel"
                clickHandler={handleCancel}
              />
              <Button
                className="seed-card-btn"
                label="Save as New"
                style={{ whiteSpace: "nowrap" }}
                clickHandler={handleSaveNew}
                disabled={!isStateValid}
              />
              {seedVariantType && !defaultEditOn && (
                <Button
                  className="seed-card-btn"
                  label="Save"
                  clickHandler={handleUpdate}
                  disabled={!isStateValid}
                />
              )}
            </Fragment>
          ) : (
            <Fragment>
              <Button
                className="seed-card-btn"
                label={"Delete"}
                clickHandler={handleDelete}
              />
              <Button
                className="seed-card-btn"
                label={"Edit"}
                clickHandler={activateEditState}
              />
            </Fragment>
          )}
        </div>
      </Card>
    </>
  );
};

export default SeedVariantTypeEditor;
